import { h } from "./dom";
import { icon } from "./icons";

export interface VideoPlayer {
  el: HTMLElement;
  video: HTMLVideoElement;
  setSource(src: string, poster?: string | null): void;
}

const clock = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = String(total % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
};

export function videoPlayer(opts: {
  src?: string | null;
  poster?: string | null;
  className?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
} = {}): VideoPlayer {
  const video = h("video", {
    class: "vp-media",
    preload: "metadata",
    playsinline: true,
    ...(opts.src ? { src: opts.src } : {}),
    ...(opts.poster ? { poster: opts.poster } : {}),
    ...(opts.autoplay ? { autoplay: true } : {}),
    ...(opts.loop ? { loop: true } : {}),
  }) as HTMLVideoElement;
  video.muted = opts.muted ?? false;
  const play = h("button", {
    class: "vp-button vp-play",
    type: "button",
    title: "Play",
    "aria-label": "Play",
  });
  const centrePlay = h("button", {
    class: "vp-centre-play",
    type: "button",
    title: "Play",
    "aria-label": "Play video",
  }, icon("play"));
  const elapsed = h("span", { class: "vp-time", text: "0:00 / 0:00" });
  const seek = h("input", {
    class: "vp-seek",
    type: "range",
    min: "0",
    max: "1000",
    value: "0",
    step: "1",
    "aria-label": "Video position",
  }) as HTMLInputElement;
  const mute = h("button", {
    class: "vp-button vp-mute",
    type: "button",
    title: "Mute",
    "aria-label": "Mute",
  });
  const volume = h("input", {
    class: "vp-volume",
    type: "range",
    min: "0",
    max: "1",
    value: "1",
    step: ".05",
    "aria-label": "Volume",
  }) as HTMLInputElement;
  const speed = h("button", {
    class: "vp-speed",
    type: "button",
    title: "Playback speed",
    "aria-label": "Playback speed, normal",
    text: "1×",
  });
  const fullscreen = h("button", {
    class: "vp-button",
    type: "button",
    title: "Full screen",
    "aria-label": "Enter full screen",
  }, icon("maximize"));
  const controls = h(
    "div",
    { class: "vp-controls" },
    play,
    seek,
    elapsed,
    mute,
    volume,
    speed,
    fullscreen,
  );
  const root = h(
    "div",
    { class: `video-player ${opts.className ?? ""}`.trim(), tabindex: "0" },
    video,
    h("div", { class: "vp-loading", "aria-hidden": "true" }),
    centrePlay,
    controls,
  );

  const setButtonIcon = (button: HTMLElement, name: string): void => {
    button.replaceChildren(icon(name));
  };
  const syncPlay = (): void => {
    const paused = video.paused;
    setButtonIcon(play, paused ? "play" : "pause");
    play.title = paused ? "Play" : "Pause";
    play.setAttribute("aria-label", play.title);
    centrePlay.hidden = !paused || video.currentTime > 0;
    root.classList.toggle("playing", !paused);
  };
  const syncTime = (): void => {
    const duration = video.duration;
    const position = duration ? video.currentTime / duration : 0;
    seek.value = String(Math.round(position * 1000));
    seek.style.setProperty("--played", `${position * 100}%`);
    elapsed.textContent = `${clock(video.currentTime)} / ${clock(duration)}`;
    seek.setAttribute("aria-valuetext", `${clock(video.currentTime)} of ${clock(duration)}`);
  };
  const syncBuffered = (): void => {
    if (!video.duration || !video.buffered.length) return;
    const end = video.buffered.end(video.buffered.length - 1);
    seek.style.setProperty("--buffered", `${Math.min(100, end / video.duration * 100)}%`);
  };
  const syncVolume = (): void => {
    const muted = video.muted || video.volume === 0;
    setButtonIcon(mute, muted ? "volume-x" : "volume");
    mute.title = muted ? "Unmute" : "Mute";
    mute.setAttribute("aria-label", mute.title);
    volume.value = String(video.muted ? 0 : video.volume);
  };
  const toggle = (): void => {
    if (video.paused) void video.play().catch(() => undefined);
    else video.pause();
  };
  const toggleFullscreen = (): void => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (root.requestFullscreen) {
      void root.requestFullscreen();
    } else {
      (video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen?.();
    }
  };

  play.addEventListener("click", toggle);
  centrePlay.addEventListener("click", toggle);
  video.addEventListener("click", toggle);
  video.addEventListener("dblclick", toggleFullscreen);
  video.addEventListener("play", syncPlay);
  video.addEventListener("pause", syncPlay);
  video.addEventListener("ended", syncPlay);
  video.addEventListener("timeupdate", syncTime);
  video.addEventListener("durationchange", syncTime);
  video.addEventListener("progress", syncBuffered);
  video.addEventListener("waiting", () => root.classList.add("buffering"));
  video.addEventListener("playing", () => root.classList.remove("buffering"));
  video.addEventListener("canplay", () => root.classList.remove("buffering"));
  seek.addEventListener("input", () => {
    if (video.duration) video.currentTime = Number(seek.value) / 1000 * video.duration;
  });
  mute.addEventListener("click", () => {
    video.muted = !video.muted;
    syncVolume();
  });
  volume.addEventListener("input", () => {
    video.volume = Number(volume.value);
    video.muted = video.volume === 0;
    syncVolume();
  });
  const speeds = [1, 1.25, 1.5, 2, 0.5];
  speed.addEventListener("click", () => {
    const next = speeds[(speeds.indexOf(video.playbackRate) + 1) % speeds.length]!;
    video.playbackRate = next;
    speed.textContent = `${next}×`;
    speed.setAttribute("aria-label", `Playback speed, ${next} times`);
  });
  fullscreen.addEventListener("click", toggleFullscreen);
  root.addEventListener("fullscreenchange", () => {
    const active = document.fullscreenElement === root;
    fullscreen.replaceChildren(icon(active ? "minimize" : "maximize"));
    fullscreen.title = active ? "Exit full screen" : "Full screen";
    fullscreen.setAttribute("aria-label", active ? "Exit full screen" : "Enter full screen");
  });
  root.addEventListener("keydown", (event) => {
    if (event.target !== root && event.target !== video) return;
    if (event.key === " " || event.key.toLowerCase() === "k") {
      event.preventDefault();
      toggle();
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      video.currentTime = Math.max(0, video.currentTime - 5);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      video.currentTime = Math.min(video.duration || Infinity, video.currentTime + 5);
    } else if (event.key.toLowerCase() === "m") {
      video.muted = !video.muted;
      syncVolume();
    } else if (event.key.toLowerCase() === "f") {
      toggleFullscreen();
    }
  });

  const setSource = (src: string, poster?: string | null): void => {
    if (video.getAttribute("src") !== src) video.src = src;
    if (poster) video.poster = poster;
    else video.removeAttribute("poster");
    seek.value = "0";
    seek.style.setProperty("--played", "0%");
    seek.style.setProperty("--buffered", "0%");
    syncPlay();
    syncTime();
  };
  syncPlay();
  syncTime();
  syncVolume();
  return { el: root, video, setSource };
}